import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge, Button, Input, Select, VisuallyHidden } from '@artosera/ui';
import {
  ETATS_DEMANDE_OUVERTURE,
  SOURCES_DEMANDE_OUVERTURE,
  type EtatDemandeOuverture,
  type SourceDemandeOuverture,
} from '@artosera/shared';
import { listerDemandesOuverture } from '../../lib/api.js';
import {
  CLE_DEMANDES,
  TAILLE_PAGE_DEMANDES,
  TONE_ETAT_DEMANDE,
  ancienneteEnJours,
  formaterDateHeure,
  libelleAnciennete,
  nomDuPays,
} from '../../lib/demandesOuverture.js';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import pageStyles from '../../styles/page.module.css';
import tableStyles from '../../styles/table.module.css';
import styles from './DemandesOuverture.module.css';

type FiltreEtat = EtatDemandeOuverture | 'tous';
type FiltreResponsable = 'tous' | 'moi' | 'personne';
type FiltreSource = SourceDemandeOuverture | 'toutes';

/** Le délai avant qu'une frappe dans la recherche parte vers l'API. */
const DELAI_RECHERCHE_MS = 300;

/**
 * `/demandes` : les demandes d'ouverture venues d'artosera.com (ARTO-188).
 * L'ordre (les plus récentes d'abord), les compteurs par état et le nombre de
 * modules retenus sont ceux de l'API ; seule l'ancienneté se déduit de la date.
 */
export function DemandesOuvertureList(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const [etat, setEtat] = useState<FiltreEtat>('tous');
  const [source, setSource] = useState<FiltreSource>('toutes');
  const [responsable, setResponsable] = useState<FiltreResponsable>('tous');
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const minuteur = window.setTimeout(() => {
      setRecherche((courante) => {
        const suivante = saisie.trim();
        if (suivante !== courante) setPage(1);
        return suivante;
      });
    }, DELAI_RECHERCHE_MS);
    return () => window.clearTimeout(minuteur);
  }, [saisie]);

  const requete = useQuery({
    queryKey: [...CLE_DEMANDES, etat, source, responsable, recherche, page],
    queryFn: () =>
      listerDemandesOuverture({
        ...(etat === 'tous' ? {} : { etat }),
        ...(source === 'toutes' ? {} : { source }),
        ...(responsable === 'tous' ? {} : { responsable }),
        ...(recherche === '' ? {} : { q: recherche }),
        page,
      }),
    placeholderData: keepPreviousData,
  });

  const lignes = requete.data?.items ?? [];
  const total = requete.data?.total ?? 0;
  const compteurs = requete.data?.compteurs;
  const toutesLesDemandes = compteurs
    ? ETATS_DEMANDE_OUVERTURE.reduce((somme, valeur) => somme + compteurs[valeur], 0)
    : null;
  const pages = Math.max(1, Math.ceil(total / TAILLE_PAGE_DEMANDES));
  const filtre =
    etat !== 'tous' || source !== 'toutes' || responsable !== 'tous' || recherche !== '';
  const aujourdhui = new Date();

  const choisirEtat = (valeur: FiltreEtat): void => {
    setEtat(valeur);
    setPage(1);
  };

  const pays = (code: string | null): string | null =>
    code === null ? null : nomDuPays(code, i18n.language);

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('demandesOuverture.titre')}</h1>
          <p>{t('demandesOuverture.texte')}</p>
        </div>
      </div>

      <fieldset className={styles.etats}>
        <VisuallyHidden asChild>
          <legend>{t('demandesOuverture.filtres.etats')}</legend>
        </VisuallyHidden>
        {(['tous', ...ETATS_DEMANDE_OUVERTURE] as const).map((valeur) => {
          const nombre = valeur === 'tous' ? toutesLesDemandes : (compteurs?.[valeur] ?? null);
          return (
            <button
              key={valeur}
              type="button"
              className={styles.etat}
              aria-pressed={etat === valeur}
              onClick={() => choisirEtat(valeur)}
            >
              {valeur === 'tous'
                ? t('demandesOuverture.filtres.toutes')
                : t(`demandesOuverture.etat.${valeur}`)}
              {nombre === null ? null : <span className={styles.compteur}>{nombre}</span>}
            </button>
          );
        })}
      </fieldset>

      <div className={tableStyles.toolbar}>
        <div className={tableStyles.champRecherche}>
          <label htmlFor="demandes-recherche">{t('demandesOuverture.filtres.recherche')}</label>
          <Input
            id="demandes-recherche"
            type="search"
            value={saisie}
            maxLength={100}
            placeholder={t('demandesOuverture.filtres.recherchePlaceholder')}
            onChange={(evenement) => setSaisie(evenement.target.value)}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="demandes-filtre-responsable">
            {t('demandesOuverture.filtres.responsable')}
          </label>
          <Select
            id="demandes-filtre-responsable"
            value={responsable}
            onValueChange={(valeur) => {
              setResponsable(valeur as FiltreResponsable);
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('demandesOuverture.filtres.tous') },
              { value: 'moi', label: t('demandesOuverture.filtres.moi') },
              { value: 'personne', label: t('demandesOuverture.filtres.personne') },
            ]}
          />
        </div>
        <div className={tableStyles.champFiltre}>
          <label htmlFor="demandes-filtre-source">{t('demandesOuverture.filtres.source')}</label>
          <Select
            id="demandes-filtre-source"
            value={source}
            onValueChange={(valeur) => {
              setSource(valeur as FiltreSource);
              setPage(1);
            }}
            options={[
              { value: 'toutes', label: t('demandesOuverture.filtres.toutesSources') },
              ...SOURCES_DEMANDE_OUVERTURE.map((valeur) => ({
                value: valeur,
                label: t(`demandesOuverture.source.${valeur}`),
              })),
            ]}
          />
        </div>
      </div>

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('demandesOuverture.chargement')}</p>
      ) : requete.isError ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('demandesOuverture.erreurChargement')}
        </p>
      ) : lignes.length === 0 ? (
        <p className={pageStyles.videEtat}>
          {filtre ? t('demandesOuverture.vide') : t('demandesOuverture.videTout')}
        </p>
      ) : (
        <>
          <TableauDefilant libelle={t('demandesOuverture.titre')} empile>
            <table className={`${tableStyles.table} ${styles.liste}`}>
              <thead>
                <tr>
                  <th scope="col">{t('demandesOuverture.colonnes.numero')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.galerie')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.contact')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.source')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.palier')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.modules')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.responsable')}</th>
                  <th scope="col">{t('demandesOuverture.colonnes.anciennete')}</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => {
                  const lieu = [ligne.galerie.ville, pays(ligne.galerie.pays)]
                    .filter((morceau): morceau is string => morceau !== null && morceau !== '')
                    .join(', ');
                  return (
                    <tr key={ligne.id}>
                      <td data-plein="">
                        <Link to={`/demandes/${ligne.id}`} className={styles.numero}>
                          {ligne.numero}
                        </Link>{' '}
                        <Badge tone={TONE_ETAT_DEMANDE[ligne.etat]}>
                          {t(`demandesOuverture.etat.${ligne.etat}`)}
                        </Badge>
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.galerie')}>
                        <span className={styles.galerie}>
                          {ligne.galerie.nom === '' ? (
                            <span className={styles.muet}>{t('demandesOuverture.sansNom')}</span>
                          ) : (
                            ligne.galerie.nom
                          )}
                        </span>
                        {lieu === '' ? null : (
                          <div className={tableStyles.celluleMuette}>{lieu}</div>
                        )}
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.contact')}>
                        {ligne.contact.nom}
                        <div className={tableStyles.celluleMuette}>{ligne.contact.email}</div>
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.source')}>
                        {t(`demandesOuverture.source.${ligne.source}`)}
                      </td>
                      <td
                        data-label={t('demandesOuverture.colonnes.palier')}
                        className={styles.nowrap}
                      >
                        {ligne.palier === null ? (
                          <span className={tableStyles.celluleMuette}>
                            {t('demandesOuverture.sansPalier')}
                          </span>
                        ) : (
                          t(`galeries.palier.${ligne.palier}`)
                        )}
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.modules')}>
                        <span className={styles.nombre}>{ligne.modulesRetenus}</span>
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.responsable')}>
                        {ligne.responsable ?? (
                          <span className={tableStyles.celluleMuette}>
                            {t('demandesOuverture.sansResponsable')}
                          </span>
                        )}
                      </td>
                      <td data-label={t('demandesOuverture.colonnes.anciennete')}>
                        <time
                          className={styles.anciennete}
                          dateTime={ligne.creeLe}
                          title={formaterDateHeure(ligne.creeLe, i18n.language)}
                        >
                          {libelleAnciennete(t, ancienneteEnJours(ligne.creeLe, aujourdhui))}
                        </time>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableauDefilant>
          {pages > 1 ? (
            <div className={tableStyles.pagination}>
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((courante) => Math.max(1, courante - 1))}
              >
                {t('demandesOuverture.pagination.precedente')}
              </Button>
              <span>{t('demandesOuverture.pagination.page', { page, total: pages })}</span>
              <Button
                variant="secondary"
                disabled={page >= pages}
                onClick={() => setPage((courante) => courante + 1)}
              >
                {t('demandesOuverture.pagination.suivante')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
